/**
 * Failure taxonomy shared by every MiraOps client.
 * Ported from MiraOps-Data-Design/RequestFailure.mjs and extended with the
 * Postgres codes the local RPCs raise. A classification is never proof of a
 * remote outcome; messages are user-safe and never contain server text.
 */

export type FailureKind =
  | 'session_expired'      // 401: refresh once, then sign in again
  | 'access_revoked'       // 42501 / 403: current rights no longer allow this
  | 'invalid_input'        // 22023 / 400 / 422 for writes
  | 'query_restart'        // 22023 on a paged read: cursor expired or query changed
  | 'revision_conflict'    // 40001 / 409: someone else changed the record
  | 'not_found'            // deleted or not visible; no existence disclosure
  | 'rate_limited'         // 429
  | 'network'              // no response at all (offline, DNS, reset)
  | 'service_unavailable'  // 502/503/504
  | 'ambiguous_outcome'    // write without retry key lost its response
  | 'cancelled'            // caller aborted
  | 'unknown';

export interface Failure {
  kind: FailureKind;
  /** Drafts, selections and uploads stay on device for every kind. */
  preserveDraft: true;
  /** Cached rows from this scope must be dropped (permission changed). */
  purgeProtectedCache: boolean;
  /** Safe to retry automatically with the same idempotency key. */
  retry: boolean;
  delayMs: number;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly hint: string | undefined;
  /** Server message token such as "revision_conflict"; never displayed raw. */
  readonly token: string | undefined;
  constructor(status: number, code?: string, token?: string, hint?: string) {
    super(`api_error_${status}${code ? `_${code}` : ''}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.token = token;
    this.hint = hint;
  }
}

export interface ClassifyInput {
  error: unknown;
  operation: 'read' | 'write';
  /** True when the write carries a durable idempotency key that will be reused. */
  hasRetryKey?: boolean;
  /** Zero-based automatic attempt count. */
  attempt?: number;
  /** True for paged reads that sent a cursor. */
  pagedWithCursor?: boolean;
  random?: () => number;
}

function retrySeconds(hint: string | undefined): number {
  const match = typeof hint === 'string' ? /^retry_after_seconds=(\d{1,4})$/.exec(hint) : null;
  const value = match ? Number(match[1]) : 30;
  return value >= 1 && value <= 3600 ? value : 30;
}

const base = { preserveDraft: true as const, purgeProtectedCache: false, retry: false, delayMs: 0 };

export function classifyFailure(input: ClassifyInput): Failure {
  const { error, operation, hasRetryKey = false, attempt = 0, pagedWithCursor = false, random = Math.random } = input;
  if (!Number.isInteger(attempt) || attempt < 0) throw Error('invalid_attempt');

  if (isAbort(error)) return { ...base, kind: 'cancelled', message: 'Cancelled.' };

  if (!(error instanceof ApiError)) {
    // No HTTP response. Reads may retry; writes are only safe with a durable key.
    return transient(operation, hasRetryKey, attempt, random, 'network');
  }
  const { status, code, token, hint } = error;

  if (status === 401 || code === 'PGRST301' || code === 'PGRST303')
    return { ...base, kind: 'session_expired', message: 'Your session needs to be renewed. Your work is preserved.' };
  if (status === 403 || code === '42501')
    return { ...base, kind: 'access_revoked', purgeProtectedCache: true, message: 'You no longer have access to this. Your work is preserved.' };
  if (status === 409 || code === '40001' || token === 'revision_conflict')
    return { ...base, kind: 'revision_conflict', message: 'This record changed. Review the latest version before saving again.' };
  if (status === 429 || token === 'rate_limited')
    return { ...base, kind: 'rate_limited', delayMs: retrySeconds(hint) * 1000, message: 'Too many requests. Wait before trying again.' };
  if (token === 'order_deleted' || status === 404)
    return { ...base, kind: 'not_found', message: 'This record is no longer available.' };
  if (status === 0 || status === 502 || status === 503 || status === 504)
    return transient(operation, hasRetryKey, attempt, random, 'service_unavailable');
  if (code === '22023' && operation === 'read' && pagedWithCursor)
    return { ...base, kind: 'query_restart', message: 'The list needs to reload from the start.' };
  if (status === 400 || status === 422 || code === '22023')
    return { ...base, kind: 'invalid_input', message: 'Some information needs correction. Your work is preserved.' };
  return { ...base, kind: 'unknown', message: 'The request could not be completed. Your work is preserved.' };
}

/**
 * Repeats a read while `classifyFailure` allows it (offline or temporarily unavailable, with its
 * back-off), up to `tries` attempts in all; a cancellation, any other failure or the last one is
 * thrown unchanged. Only for reads, which are safe to repeat.
 */
export async function retryRead<T>(read: () => Promise<T>, options: { tries?: number; sleep?: (ms: number) => Promise<void>; random?: () => number } = {}): Promise<T> {
  const tries = options.tries ?? 3;
  const sleep = options.sleep ?? (ms => new Promise<void>(done => setTimeout(done, ms)));
  for (let attempt = 0; ; attempt++) {
    try { return await read(); }
    catch (error) {
      const failure = classifyFailure({ error, operation: 'read', attempt, random: options.random });
      if (!failure.retry || attempt + 1 >= tries) throw error;
      await sleep(failure.delayMs);
    }
  }
}

function transient(
  operation: 'read' | 'write', hasRetryKey: boolean, attempt: number, random: () => number,
  kind: 'network' | 'service_unavailable',
): Failure {
  const safe = operation === 'read' || hasRetryKey;
  if (!safe)
    return { ...base, kind: 'ambiguous_outcome', message: 'The outcome is not confirmed. Check the latest state before trying again.' };
  const message = kind === 'network'
    ? 'You appear to be offline. Your work is preserved.'
    : 'The service is temporarily unavailable. Your work is preserved.';
  if (attempt >= 3) return { ...base, kind, message };
  const jitter = random();
  if (!Number.isFinite(jitter) || jitter < 0 || jitter > 1) throw Error('invalid_jitter');
  return { ...base, kind, retry: true, delayMs: Math.round(Math.min(8000, 500 * 2 ** attempt) * (0.5 + jitter * 0.5)), message };
}

function isAbort(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: string }).name === 'AbortError';
}
